import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ------------------ THUNK: Tüm testleri getir ------------------
export const getTests = createAsyncThunk(
  "questions/getTests",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Fetching tests from API...");
      const res = await axios.get(`${API_URL}/tests`);
      console.log("API Response:", res.data);
      return res.data.data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data?.message || "Tests not found");
    }
  },
);

// ------------------ THUNK: Tüm soruları getir ------------------
export const getQuestions = createAsyncThunk(
  "questions/getQuestions",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Fetching questions from API...");
      const res = await axios.get(`${API_URL}/questions`);
      console.log("Questions API Response:", res.data);
      return res.data.data || res.data;
    } catch (err) {
      console.error("Questions API Error:", err);
      return rejectWithValue(
        err.response?.data?.message || "Questions not found",
      );
    }
  },
);

// ------------------ THUNK: Tek bir test ve soruları getir ------------------
export const getTestById = createAsyncThunk(
  "questions/getTestById",
  async (testId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/tests/${testId}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Test details not found",
      );
    }
  },
);

// ------------------ THUNK: Kullanıcının test yanıtlarını gönder ------------------
export const submitTestResponses = createAsyncThunk(
  "questions/submitTestResponses",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/tests/responses`, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Answer not sent");
    }
  },
);

// ------------------ THUNK: Kullanıcının geçmiş test sonuçlarını al ------------------
export const getUserTestResults = createAsyncThunk(
  "questions/getUserTestResults",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${API_URL}/tests/users/${userId}/test-results`,
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Test results not found",
      );
    }
  },
);

// ------------------ THUNK: Yeni bir test oluştur ------------------
export const createTest = createAsyncThunk(
  "questions/createTest",
  async (testData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/tests`, testData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Test not created");
    }
  },
);

// ------------------ THUNK: Bir teste soru ekle ------------------
export const addQuestionToTest = createAsyncThunk(
  "questions/addQuestionToTest",
  async ({ testId, question }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/tests/${testId}/questions`,
        question,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Question not added",
      );
    }
  },
);

// ------------------ THUNK: Admin - Soruları güncelle ------------------
export const updateQuestions = createAsyncThunk(
  "questions/updateQuestions",
  async ({ testId, updatedQuestions }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${API_URL}/tests/${testId}`, {
        questions: updatedQuestions,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue("Questions not updated");
    }
  },
);

// ------------------ INITIAL STATE ------------------
const initialState = {
  tests: [],
  selectedTest: null,
  questions: [],
  userResponses: [],
  result: null,
  loading: false,
  error: null,
};

// ------------------ SLICE ------------------
const questionSlice = createSlice({
  name: "questions",
  initialState,
  reducers: {
    setLocalQuestions: (state, action) => {
      state.questions = action.payload;
    },
    clearSelectedTest: (state) => {
      state.selectedTest = null;
      state.questions = [];
    },
    clearResponses: (state) => {
      state.userResponses = [];
      state.result = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all tests
      .addCase(getTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTests.fulfilled, (state, action) => {
        state.loading = false;
        state.tests = action.payload;
      })
      .addCase(getTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get one test + its questions
      .addCase(getTestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTestById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTest = action.payload.test;
        state.questions = action.payload.test.questionIds; // Dokümana göre burada sorular da var
      })
      .addCase(getTestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Submit responses
      .addCase(submitTestResponses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitTestResponses.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
      })
      .addCase(submitTestResponses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get user results
      .addCase(getUserTestResults.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserTestResults.fulfilled, (state, action) => {
        state.loading = false;
        state.userResponses = action.payload;
      })
      .addCase(getUserTestResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create test
      .addCase(createTest.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add question to test
      .addCase(addQuestionToTest.pending, (state) => {
        state.loading = true;
      })
      .addCase(addQuestionToTest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addQuestionToTest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admin: Update questions
      .addCase(updateQuestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateQuestions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// ------------------ EXPORTS ------------------
export const { setLocalQuestions, clearSelectedTest, clearResponses } =
  questionSlice.actions;
export default questionSlice.reducer;
